/**
 * Guides — static bilingual explainers for the four platform pillars:
 * Find Suppliers · Verify Evidence · Claim or Confirm a Plant · Request Evidence.
 * Frontend-only content (V1 refresh, decision D). No API writes.
 */
import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Search, FileText, Building2, Lock, Clock, ShieldCheck } from "lucide-react";
import { useI18n } from "../i18n";
import useSeoHead from "../useSeoHead";
import {
  GovListedBadge,
  PendingVerificationBadge,
  EvidenceBackedBadge,
  CorridorVerifiedBadge,
} from "./adminUiHelpers";

const GUIDES = {
  "find-suppliers": {
    icon: Search,
    title: { en: "How to Find Suppliers", es: "Cómo encontrar proveedores" },
    intro: {
      en: "Corridor Digital lists government-listed IMMEX plant locations in the Chihuahua–El Paso corridor. Every plant location is shown separately — even when the same legal entity operates several plants.",
      es: "Corridor Digital lista ubicaciones de planta IMMEX listadas por gobierno en el corredor Chihuahua–El Paso. Cada ubicación de planta se muestra por separado — incluso cuando la misma entidad legal opera varias plantas.",
    },
    steps: {
      en: [
        ["Browse the plant directory", "Open the Suppliers page to see every published plant location, with its plant sequence (P01, P02…), address, and IMMEX reference."],
        ["Filter by capability category", "Use the capability categories to narrow results. Categories only show plants with real, evidence-backed or self-declared capabilities — nothing is inferred."],
        ["Read the trust labels", "Every result shows exactly what is and isn't verified. See the Verify Evidence guide for what each label means."],
        ["Send an RFQ", "Each plant profile has a direct inquiry form. Your request goes to the plant through the platform — no middlemen."],
      ],
      es: [
        ["Explora el directorio de plantas", "Abre la página de Proveedores para ver cada ubicación de planta publicada, con su secuencia de planta (P01, P02…), dirección y referencia IMMEX."],
        ["Filtra por categoría de capacidad", "Usa las categorías de capacidad para acotar resultados. Las categorías solo muestran plantas con capacidades reales, respaldadas por evidencia o auto-declaradas — nada se infiere."],
        ["Lee las etiquetas de confianza", "Cada resultado muestra exactamente qué está verificado y qué no. Consulta la guía Verificar Evidencia para el significado de cada etiqueta."],
        ["Envía una cotización (RFQ)", "Cada perfil de planta tiene un formulario directo. Tu solicitud llega a la planta por la plataforma — sin intermediarios."],
      ],
    },
    cta: { to: "/plants", en: "Open the plant directory", es: "Abrir el directorio de plantas" },
  },
  "verify-evidence": {
    icon: FileText,
    title: { en: "How to Verify Evidence", es: "Cómo verificar evidencia" },
    intro: {
      en: "Four trust labels appear across the platform. They are never conflated — each answers a different question about a plant record.",
      es: "Cuatro etiquetas de confianza aparecen en la plataforma. Nunca se mezclan — cada una responde una pregunta distinta sobre un registro de planta.",
    },
    labels: true,
    steps: {
      en: [
        ["Government-listed IMMEX", "The record comes from the official Mexican government IMMEX listing. It says nothing about Corridor verification."],
        ["Evidence-backed capability", "One or more capabilities carry cited research evidence: a source URL, the exact supporting text, source type, confidence, and the date checked — tied to this specific plant location."],
        ["Corridor verification pending", "Plant-specific Corridor verification has not been completed. This is the default state for most listings."],
        ["Corridor Verified", "A Corridor reviewer confirmed plant-level evidence through the Compliance Vault ceremony: reviewer identity, evidence notes, document scope, issue and expiration dates — all on file."],
      ],
      es: [
        ["IMMEX listado por gobierno", "El registro proviene del listado oficial IMMEX del gobierno mexicano. No dice nada sobre la verificación Corridor."],
        ["Capacidad respaldada por evidencia", "Una o más capacidades tienen evidencia de investigación citada: URL de fuente, texto exacto de respaldo, tipo de fuente, confianza y fecha de consulta — vinculada a esta ubicación de planta específica."],
        ["Verificación Corridor pendiente", "La verificación Corridor específica de la planta no se ha completado. Es el estado por defecto de la mayoría de los listados."],
        ["Corridor Verified", "Un revisor de Corridor confirmó evidencia a nivel de planta mediante la ceremonia de la Bóveda de Cumplimiento: identidad del revisor, notas de evidencia, alcance del documento, fechas de emisión y vencimiento — todo en el expediente."],
      ],
    },
    cta: { to: "/digest", en: "Browse the capability index", es: "Explorar el índice de capacidades" },
  },
  "claim-your-plant": {
    icon: Building2,
    title: { en: "How to Claim or Confirm Your Plant", es: "Cómo reclamar o confirmar tu planta" },
    intro: {
      en: "If you work at a listed plant, you can confirm the record is accurate, flag corrections, propose capabilities, and attach one private document. Nothing changes publicly until a reviewer approves — and your listing status never changes automatically.",
      es: "Si trabajas en una planta listada, puedes confirmar que el registro es correcto, señalar correcciones, proponer capacidades y adjuntar un documento privado. Nada cambia públicamente hasta que un revisor aprueba — y el estatus de tu listado nunca cambia automáticamente.",
    },
    steps: {
      en: [
        ["Find your exact plant location", "Search the plant directory for your plant. Repeated company names are separate P-locations — claim the exact one."],
        ["Open \"Claim or Confirm This Profile\"", "Every plant profile has the claim button. The form shows the record name, plant sequence, address, and IMMEX reference so you know you have the right one."],
        ["Submit and confirm your email", "You'll receive a confirmation link valid for 7 days. Your submission only enters the review queue after you confirm."],
        ["Manual review", "A Corridor reviewer examines your submission. Proposed capabilities stay proposed — they never change public categories automatically. You'll see a reference ID at every step."],
      ],
      es: [
        ["Encuentra tu ubicación de planta exacta", "Busca tu planta en el directorio. Los nombres de empresa repetidos son ubicaciones P por separado — reclama la exacta."],
        ["Abre \"Reclamar o confirmar este perfil\"", "Cada perfil de planta tiene el botón de reclamo. El formulario muestra el nombre del registro, la secuencia de planta, la dirección y la referencia IMMEX para que sepas que es la correcta."],
        ["Envía y confirma tu correo", "Recibirás un enlace de confirmación válido por 7 días. Tu envío solo entra a la cola de revisión después de confirmar."],
        ["Revisión manual", "Un revisor de Corridor examina tu envío. Las capacidades propuestas permanecen propuestas — nunca cambian las categorías públicas automáticamente. Verás un ID de referencia en cada paso."],
      ],
    },
    cta: { to: "/plants", en: "Find your plant", es: "Encuentra tu planta" },
  },
  "request-evidence": {
    icon: Lock,
    title: { en: "How to Request Evidence", es: "Cómo solicitar evidencia" },
    intro: {
      en: "Compliance documents live in the Corridor Compliance Vault. Public pages show only safe metadata — document type, scope, dates, and status. The files themselves are private.",
      es: "Los documentos de cumplimiento viven en la Bóveda de Cumplimiento de Corridor. Las páginas públicas muestran solo metadatos seguros — tipo de documento, alcance, fechas y estatus. Los archivos son privados.",
    },
    steps: {
      en: [
        ["Open a plant profile", "Plants with evidence on file show an evidence panel listing each document's type, scope, and validity dates."],
        ["Request access", "Signed-in users can request access to a specific document, stating the purpose. Requests are rate-limited and audited."],
        ["Review and grant", "A Corridor reviewer approves or denies each request individually. Grants are scoped and revocable, and every action is recorded in the audit trail."],
        ["No status changes", "Requesting or receiving access never changes a plant's public labels or listing status."],
      ],
      es: [
        ["Abre un perfil de planta", "Las plantas con evidencia en expediente muestran un panel con el tipo, alcance y fechas de validez de cada documento."],
        ["Solicita acceso", "Los usuarios con sesión pueden solicitar acceso a un documento específico, indicando el propósito. Las solicitudes tienen límite de frecuencia y se auditan."],
        ["Revisión y otorgamiento", "Un revisor de Corridor aprueba o niega cada solicitud individualmente. Los accesos son delimitados y revocables, y cada acción queda en la bitácora de auditoría."],
        ["Sin cambios de estatus", "Solicitar o recibir acceso nunca cambia las etiquetas públicas ni el estatus del listado de una planta."],
      ],
    },
    cta: { to: "/directory", en: "Browse the directory", es: "Explorar el directorio" },
  },
};

const ORDER = ["find-suppliers", "verify-evidence", "claim-your-plant", "request-evidence"];

export default function Guides() {
  const { slug } = useParams();
  const { lang } = useI18n();
  const g = slug ? GUIDES[slug] : null;

  useSeoHead({
    title: g
      ? `${g.title[lang]} | Corridor Digital`
      : lang === "es" ? "Guías | Corridor Digital" : "Guides | Corridor Digital",
    description: g
      ? g.intro[lang].slice(0, 155)
      : lang === "es"
        ? "Guías de Corridor Digital: encontrar proveedores, verificar evidencia, reclamar tu planta y solicitar evidencia."
        : "Corridor Digital guides: find suppliers, verify evidence, claim your plant, and request evidence.",
    path: slug ? `/guides/${slug}` : "/guides",
    lang,
  });

  if (slug && !g) {
    return (
      <div className="container-cd py-20" data-testid="guide-not-found">
        <h1 className="font-display text-4xl">{lang === "es" ? "Guía no encontrada" : "Guide not found"}</h1>
        <Link to="/guides" className="btn btn-primary mt-6 inline-flex">
          {lang === "es" ? "Ver todas las guías" : "See all guides"} <ArrowRight size={14} />
        </Link>
      </div>
    );
  }

  if (!g) {
    return (
      <div className="container-cd py-16" data-testid="guides-index-page">
        <div className="overline">{lang === "es" ? "GUÍAS · CORRIDOR DIGITAL" : "GUIDES · CORRIDOR DIGITAL"}</div>
        <h1 className="font-display text-5xl md:text-6xl mt-4 leading-[0.95] max-w-3xl">
          {lang === "es" ? "Cómo funciona el directorio de registro." : "How the directory of record works."}
        </h1>
        <div className="mt-12 grid md:grid-cols-2 gap-0" style={{ border: "1px solid var(--line)" }}>
          {ORDER.map((s, i) => {
            const gg = GUIDES[s];
            const Icon = gg.icon;
            return (
              <Link
                key={s}
                to={`/guides/${s}`}
                className="p-8 bg-white block cd-card-interactive"
                style={{
                  borderRight: i % 2 === 0 ? "1px solid var(--line)" : "none",
                  borderBottom: i < 2 ? "1px solid var(--line)" : "none",
                }}
                data-testid={`guide-card-${s}`}
              >
                <Icon size={20} style={{ color: "var(--orange)" }} />
                <h2 className="font-heading text-lg font-semibold mt-4">{gg.title[lang]}</h2>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--ink-2)" }}>{gg.intro[lang]}</p>
                <div className="mt-4 inline-flex items-center gap-1.5 font-mono text-xs" style={{ color: "var(--orange)" }}>
                  {lang === "es" ? "Leer guía" : "Read guide"} <ArrowRight size={12} />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    );
  }

  const Icon = g.icon;
  return (
    <div className="container-cd py-16 max-w-4xl" data-testid={`guide-page-${slug}`}>
      <Link to="/guides" className="font-mono text-xs underline" style={{ color: "var(--ink-2)" }} data-testid="guide-back-link">
        ← {lang === "es" ? "Todas las guías" : "All guides"}
      </Link>
      <div className="mt-6 flex items-center gap-3">
        <Icon size={24} style={{ color: "var(--orange)" }} />
        <div className="overline">{lang === "es" ? "GUÍA" : "GUIDE"}</div>
      </div>
      <h1 className="font-display text-4xl md:text-6xl mt-3 leading-[0.95]" data-testid="guide-title">{g.title[lang]}</h1>
      <p className="mt-6 text-lg leading-relaxed" style={{ color: "var(--ink-2)" }} data-testid="guide-intro">{g.intro[lang]}</p>

      {g.labels ? (
        <div className="mt-8 cd-card p-6" data-testid="guide-labels-legend">
          <div className="overline mb-4">{lang === "es" ? "LAS CUATRO ETIQUETAS" : "THE FOUR LABELS"}</div>
          <div className="flex flex-wrap gap-2">
            <GovListedBadge lang={lang} testid="guide-badge-gov" />
            <EvidenceBackedBadge lang={lang} testid="guide-badge-evidence" />
            <PendingVerificationBadge lang={lang} testid="guide-badge-pending" />
            <CorridorVerifiedBadge testid="guide-badge-corridor" />
          </div>
        </div>
      ) : null}

      <ol className="mt-10 space-y-8" data-testid="guide-steps">
        {g.steps[lang].map(([h, b], i) => (
          <li key={i} className="flex gap-5">
            <div
              className="font-mono flex-shrink-0"
              style={{ color: "var(--orange)", fontSize: 15, fontWeight: 700, letterSpacing: "0.08em", paddingTop: 2 }}
            >
              {String(i + 1).padStart(2, "0")}
            </div>
            <div>
              <h2 className="font-heading text-lg font-semibold">{h}</h2>
              <p className="mt-1.5 text-sm leading-relaxed" style={{ color: "var(--ink-2)" }}>{b}</p>
            </div>
          </li>
        ))}
      </ol>

      <div className="mt-12 pt-8" style={{ borderTop: "1px solid var(--line)" }}>
        <Link to={g.cta.to} className="btn btn-primary btn-lg inline-flex" data-testid="guide-cta">
          {g.cta[lang]} <ArrowRight size={16} />
        </Link>
      </div>
      <p className="mt-8 font-mono text-xs flex items-center gap-2" style={{ color: "var(--ink-2)" }}>
        <ShieldCheck size={12} />
        {lang === "es"
          ? "Corridor Verified se otorga únicamente mediante la revisión de evidencia de la Bóveda de Cumplimiento. Ningún pago ni reclamo cambia una etiqueta de confianza."
          : "Corridor Verified is granted only through the Compliance Vault evidence review. No payment or claim ever changes a trust label."}
      </p>
    </div>
  );
}
